import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Table, { TableColumn } from '../../../../components/organisms/table';
import MVCHeader from '../../../../components/organisms/mvc-header';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import Actions from '../../../../components/molecules/actions';
import { ModalProps } from '../../../../components/template/mvc-modal';
import classNames from 'classnames';
import ShipsModal from './modal';
import ShipService, { IShip } from '../../../../services/Ship.service';
import { useRouteLoaderData } from 'react-router-dom';
import { HomeLoaderData } from '../../../../types/homeLoaderData';

const initialState: IShip = {
	name: '',
	owner: '',
	isActive: true,
	client: {},
};

const AuditoryShip = () => {
	const { t } = useTranslation();
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const modalTitles = {
		create: t('port_management.ships.new'),
		edit: t('port_management.ships.edit'),
		view: t('port_management.ships.view'),
	};

	const [data, setData] = useState<IShip[]>([]);

	const [paginationOptions, setPaginationOptions] = useState<PaginationProps>(
		{
			pageCurrent: 1,
			pageSize: 10,
			totalItems: 0,
			totalPages: 1,
		}
	);

	const [modalProps, setModalProps] = useState<ModalProps<IShip>>({
		data: initialState,
		title: modalTitles.view,
		mode: 'view',
		isModalVisible: false,
		onClose: (reload?: boolean) => {
			setModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: initialState,
			}));
			if (reload) fetchData();
		},
	});

	const fetchData = useCallback(async () => {
		const response = await ShipService.getAll({
			pageCurrent: paginationOptions.pageCurrent,
			pageSize: paginationOptions.pageSize,
			idClient: Number(accountId),
		});
		if (response) {
			setData(response.data);
			setPaginationOptions((prev) => ({
				...prev,
				totalItems: response.totalItems,
				totalPages: response.totalPages,
			}));
		}
	}, [paginationOptions.pageCurrent, paginationOptions.pageSize]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const onPaginationUpdate = (pageSize: number, pageCurrent: number) => {
		setPaginationOptions((prev) => ({ ...prev, pageSize, pageCurrent }));
	};

	const onActionsViewClick = useCallback(
		(data: IShip) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.view,
				mode: 'view',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.view]
	);

	const onActionsEditClick = useCallback(
		(data: IShip) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.edit,
				mode: 'edit',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.edit]
	);

	const onCreateClick = useCallback(() => {
		setModalProps((prev) => ({
			...prev,
			title: modalTitles.create,
			mode: 'create',
			isModalVisible: true,
			data: initialState,
		}));
	}, [modalTitles.create]);

	const columns: TableColumn<IShip>[] = [
		{ key: 'name', label: t('port_management.ship') },
		{ key: 'owner', label: t('port_management.ships.owner') },
		{
			key: 'actions',
			label: t('misc.actions'),
			children: (row) => (
				<Actions
					onViewClick={() => onActionsViewClick(row)}
					onEditClick={() => onActionsEditClick(row)}
				/>
			),
		},
	];

	return (
		<>
			<div className="flex-grow grid grid-rows-[auto_1fr_auto] p-4 gap-y-4">
				<MVCHeader
					title={t('port_management.ships.list')}
					onCreateText={t('port_management.ships.new')}
					onCreateClick={onCreateClick}
					onCreateDisabled={!accountId}
				/>

				<div className="overflow-x-auto w-full">
					<Table
						data={data}
						columns={columns}
						className={classNames('grid-cols-[1fr_1fr_auto]')}
					/>
				</div>

				<Pagination
					{...paginationOptions}
					onUpdate={onPaginationUpdate}
				/>
			</div>

			<ShipsModal setModalProps={setModalProps} {...modalProps} />
		</>
	);
};

export default AuditoryShip;
